import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Icono } from "./iconos";
import { defaultFilters, fetchCampaignOptions, fetchDashboard } from "./dashboardApi";
import { useMetaAdAccounts } from "./metaAdAccounts";
import {
    ZMLayout,
    ZMCard,
    ZMChartBox,
    ZMMetricCard,
    ZMTable,
    ZMBadge,
    ZMFiltersBar,
    ZMEmptyState,
    ZMLoadingState,
    ZMErrorState,
    buildZmChartOptions,
} from "./components/ZMUI";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const fmtNum = (n, dec = 0) =>
    n == null ? "0" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtUsd = (n) => `US$ ${fmtNum(n || 0, 2)}`;
const fmtPct = (n) => `${fmtNum(n || 0, 2)}%`;

export default function ZMResumen() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState("");
    const [datos, setDatos] = useState(null);
    const [filters, setFilters] = useState(() => defaultFilters());
    const [draftFilters, setDraftFilters] = useState(() => defaultFilters());
    const [campaignOptions, setCampaignOptions] = useState([]);
    const { accounts, selectedAccount, selectedKey, selectAccount, loading: loadingAccounts } = useMetaAdAccounts();

    const cargar = async (nextFilters = filters) => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        if (!selectedAccount) return;
        setCargando(true);
        setError("");
        try {
            const [payload, optionItems] = await Promise.all([
                fetchDashboard("resumen-ejecutivo", token, nextFilters),
                fetchCampaignOptions(token, nextFilters),
            ]);
            setDatos(payload);
            setCampaignOptions(optionItems);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudo cargar el resumen ejecutivo.");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, selectedKey]);

    const kpis = datos?.kpis || {};
    const auditoria = datos?.auditoriaLeads || {};
    const embudo = datos?.embudoConversion || {};
    const recomendaciones = datos?.recomendaciones || [];
    const trend = useMemo(() => datos?.tendenciaDiaria || [], [datos]);
    const topCampanas = datos?.topCampanas || {};
    const ultimosLeads = datos?.ultimosLeads || [];

    const trendData = useMemo(() => ({
        labels: trend.map((x) => x.fecha),
        datasets: [
            {
                type: "line",
                label: "Leads CRM",
                data: trend.map((x) => x.leads || 0),
                borderColor: "#1f2937",
                backgroundColor: "rgba(31, 41, 55, 0.10)",
                tension: 0.38,
                borderWidth: 3,
                pointRadius: 2.5,
                pointHoverRadius: 5,
                pointBackgroundColor: "#1f2937",
                yAxisID: "y",
            },
            {
                type: "bar",
                label: "Gasto",
                data: trend.map((x) => x.gasto || 0),
                backgroundColor: "rgba(180, 211, 47, 0.78)",
                borderColor: "rgba(154, 197, 0, 0.95)",
                borderWidth: 1,
                borderRadius: 10,
                maxBarThickness: 34,
                yAxisID: "y1",
            },
        ],
    }), [trend]);

    if (cargando) return <ZMLoadingState label="Cargando resumen ejecutivo..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={() => cargar(filters)} />;
    if (!datos) return <ZMEmptyState title="Sin datos de resumen" hint="No hay información disponible para el período." />;

    const metaOk = !!datos?.meta?.disponible;
    const embudoRows = [
        { label: "Leads recibidos", value: embudo.leadsRecibidos || 0 },
        { label: "Contactados", value: embudo.contactados || 0 },
        { label: "Calificados", value: embudo.calificados || 0 },
        { label: "Demo agendada", value: embudo.demoAgendada || 0 },
        { label: "Cerrados", value: embudo.cerradosActivados || 0 },
    ];
    const maxEmbudo = Math.max(...embudoRows.map((x) => x.value), 1);

    return (
        <ZMLayout
            title="Resumen ejecutivo"
            subtitle="Vista central para dirección, paid media, marketing y closers."
            right={selectedAccount ? `${selectedAccount.name} (${selectedAccount.adAccountId})` : (datos?.meta?.mensaje || "Actualizado")}
            className={darkMode ? "container-dark" : ""}
        >
            <ZMFiltersBar className="mb-3">
                <div style={{ minWidth: 280 }}>
                    <label>Cuenta publicitaria</label>
                    <select className="form-select form-select-sm" value={selectedKey} onChange={(e) => selectAccount(e.target.value)} disabled={loadingAccounts || accounts.length === 0}>
                        {accounts.map((item) => <option key={item.key} value={item.key}>{item.name} ({item.adAccountId})</option>)}
                    </select>
                </div>
                <div>
                    <label>Desde</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaDesde || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaDesde: e.target.value }))} />
                </div>
                <div>
                    <label>Hasta</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaHasta || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaHasta: e.target.value }))} />
                </div>
                <div style={{ minWidth: 260 }}>
                    <label>Campaña</label>
                    <select className="form-select form-select-sm" value={draftFilters.campania || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, campania: e.target.value }))}>
                        <option value="">Todas las campañas</option>
                        {campaignOptions.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </select>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={() => {
                        setFilters({ ...draftFilters });
                        cargar({ ...draftFilters });
                    }}>
                        Aplicar
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                        const reset = defaultFilters();
                        setDraftFilters(reset);
                        setFilters(reset);
                        cargar(reset);
                    }}>
                        Limpiar
                    </button>
                </div>
            </ZMFiltersBar>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.Users />} label="Leads CRM" value={fmtNum(kpis.leadsCrm ?? kpis.leadsTotales, 0)} helper="Fuente de verdad del panel" />
                <ZMMetricCard icon={<Icono.Wallet />} label="Inversión" value={fmtUsd(kpis.inversionTotal)} empty={!metaOk} />
                <ZMMetricCard icon={<Icono.Dollar />} label="CPL CRM" value={fmtUsd(kpis.cplPromedio)} empty={!metaOk} />
                <ZMMetricCard icon={<Icono.Dollar />} label="CPC" value={fmtUsd(kpis.cpcPromedio)} empty={!metaOk} />
                <ZMMetricCard icon={<Icono.BarChart />} label="CPM" value={fmtUsd(kpis.cpmPromedio)} empty={!metaOk} />
                <ZMMetricCard icon={<Icono.MousePointer />} label="CTR" value={fmtPct(kpis.ctrPromedio)} empty={!metaOk} />
            </div>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.BarChart />} label="Leads únicos" value={fmtNum(auditoria.leadsUnicos, 0)} helper="Deduplicados por leadgen_id/teléfono/email" />
                <ZMMetricCard icon={<Icono.AlertTriangle />} label="Duplicados" value={fmtNum(auditoria.leadsDuplicados, 0)} helper="No cuentan doble en análisis" />
                <ZMMetricCard icon={<Icono.Phone />} label="Contactados" value={fmtNum(kpis.leadsContactados, 0)} />
                <ZMMetricCard icon={<Icono.CheckCircle />} label="Cerrados" value={fmtNum(kpis.leadsCerrados, 0)} />
                <ZMMetricCard icon={<Icono.Target />} label="Tasa contacto" value={fmtPct(kpis.tasaContacto)} />
                <ZMMetricCard icon={<Icono.TrendingUp />} label="Tasa cierre" value={fmtPct(kpis.tasaCierre)} />
                <ZMMetricCard icon={<Icono.Clock />} label="Tiempo resp." value={kpis.tiempoPromedioRespuestaMinutos ? `${fmtNum(kpis.tiempoPromedioRespuestaMinutos, 1)} min` : "sin datos"} />
                <ZMMetricCard icon={<Icono.Star />} label="Pipeline estimado" value={fmtUsd(kpis.valorEstimadoPipeline)} />
            </div>

            <ZMCard className="mb-3" bodyClassName="py-2">
                <div className="zm-inline-note">
                    <strong>Fuente única:</strong> el resumen usa <b>leads CRM</b> desde LeadsMeta + ficha del closer. Meta aporta inversión, impresiones, alcance, clicks y breakdowns, pero no reemplaza el conteo comercial del panel.
                </div>
            </ZMCard>

            <div className="row g-3 mb-3">
                <div className="col-12 col-xl-8">
                    <ZMCard title="Tendencia diaria de leads y gasto" subtitle="Evolución diaria del embudo superior">
                        {trend.length > 0 ? (
                            <ZMChartBox>
                                <Bar
                                    data={trendData}
                                    options={buildZmChartOptions({
                                        leftTitle: "Leads CRM",
                                        rightTitle: "US$",
                                        showRightAxis: true,
                                        darkMode,
                                    })}
                                />
                            </ZMChartBox>
                        ) : (
                            <ZMEmptyState title="Sin tendencia disponible" hint="No hubo datos suficientes para graficar este período." />
                        )}
                    </ZMCard>
                </div>
                <div className="col-12 col-xl-4">
                    <ZMCard title="Embudo de conversión" subtitle="Lectura rápida del circuito comercial" className="h-100">
                        <div className="zm-funnel-list">
                            {embudoRows.map((item) => (
                                <div key={item.label} className="zm-funnel-item">
                                    <div className="d-flex justify-content-between">
                                        <strong>{item.label}</strong>
                                        <span>{fmtNum(item.value, 0)}</span>
                                    </div>
                                    <div className="zm-progress-line">
                                        <div style={{ width: `${Math.max((item.value / maxEmbudo) * 100, item.value > 0 ? 10 : 0)}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ZMCard>
                </div>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-lg-6">
                    <ZMCard title="Top campañas por leads CRM">
                        <ZMTable
                            emptyText="Sin campañas con leads."
                            columns={[
                                { key: "campaignName", header: "Campaña" },
                                { key: "leads", header: "Leads CRM", cellClassName: "text-end", render: (x) => fmtNum(x.leads, 0) },
                                { key: "cpl", header: "CPL CRM", cellClassName: "text-end", render: (x) => fmtUsd(x.cpl) },
                                { key: "spend", header: "Inversión", cellClassName: "text-end", render: (x) => fmtUsd(x.spend) },
                            ]}
                            rows={topCampanas.porLeads || []}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-lg-6">
                    <ZMCard title="Top campañas por CPL CRM">
                        <ZMTable
                            emptyText="Sin campañas con CPL."
                            columns={[
                                { key: "campaignName", header: "Campaña" },
                                { key: "cpl", header: "CPL CRM", cellClassName: "text-end", render: (x) => fmtUsd(x.cpl) },
                                { key: "leads", header: "Leads CRM", cellClassName: "text-end", render: (x) => fmtNum(x.leads, 0) },
                                { key: "ctr", header: "CTR", cellClassName: "text-end", render: (x) => fmtPct(x.ctr) },
                            ]}
                            rows={topCampanas.porCpl || []}
                        />
                    </ZMCard>
                </div>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-lg-6">
                    <ZMCard title="Mejor CTR">
                        <ZMTable
                            emptyText="Sin datos de CTR."
                            columns={[
                                { key: "campaignName", header: "Campaña" },
                                { key: "ctr", header: "CTR", cellClassName: "text-end", render: (x) => fmtPct(x.ctr) },
                                { key: "leads", header: "Leads CRM", cellClassName: "text-end", render: (x) => fmtNum(x.leads, 0) },
                            ]}
                            rows={topCampanas.mejorCtr || []}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-lg-6">
                    <ZMCard title="Peor rendimiento">
                        <ZMTable
                            emptyText="Sin campañas para revisar."
                            columns={[
                                { key: "campaignName", header: "Campaña" },
                                { key: "cpl", header: "CPL CRM", cellClassName: "text-end", render: (x) => fmtUsd(x.cpl) },
                                { key: "ctr", header: "CTR", cellClassName: "text-end", render: (x) => fmtPct(x.ctr) },
                                { key: "spend", header: "Inversión", cellClassName: "text-end", render: (x) => fmtUsd(x.spend) },
                            ]}
                            rows={topCampanas.peorRendimiento || []}
                        />
                    </ZMCard>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-12 col-xl-8">
                    <ZMCard title="Últimos leads">
                        <ZMTable
                            emptyText="Sin leads recientes."
                            columns={[
                                { key: "fechaRecibido", header: "Fecha", render: (l) => new Date(l.fechaRecibido).toLocaleString("es-AR") },
                                { key: "nombre", header: "Nombre", render: (l) => l.nombre || "-" },
                                { key: "telefono", header: "Teléfono", render: (l) => l.telefono || "-" },
                                { key: "email", header: "Email", render: (l) => l.email || "-" },
                                { key: "estado", header: "Estado", render: (l) => <ZMBadge status={l.estado} /> },
                            ]}
                            rows={ultimosLeads}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-xl-4">
                    <ZMCard title="Alertas inteligentes">
                        {recomendaciones.length === 0 ? (
                            <ZMEmptyState title="Sin alertas activas" hint="No se detectaron desvíos relevantes en el período actual." />
                        ) : (
                            <div className="zm-alert-stack">
                                {recomendaciones.map((item, index) => (
                                    <div key={`${item.tipo}-${index}`} className="zm-alert-item">
                                        <div className="d-flex justify-content-between gap-2">
                                            <div>
                                                <strong>{String(item.tipo || "insight").replaceAll("_", " ")}</strong>
                                                <p>{item.mensaje}</p>
                                            </div>
                                            <ZMBadge
                                                status={item.severidad === "alta" ? "rechazado" : item.severidad === "media" ? "contactado" : "cerrado"}
                                                label={item.severidad || "info"}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ZMCard>
                </div>
            </div>
        </ZMLayout>
    );
}
